import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import formatCurrency from '@/shared/utils/formatCurrency';

type DetailsProps = {
  defaultExpanded?: boolean;
  shippingPrice: string;
  orderTotal: string;
  subTotal: string;
  discount: string;
  tax: string;
  currency?: string;
};

const Details: FC<DetailsProps> = ({
  defaultExpanded = false,
  shippingPrice,
  orderTotal,
  subTotal,
  currency = 'CAD',
  discount,
  tax,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  const parseValue = (value?: string) => {
    const initialValue = value || '0';
    return isNaN(parseFloat(initialValue)) ? 0 : parseFloat(initialValue);
  };

  const parsedValues = {
    shippingPrice: parseValue(shippingPrice),
    orderTotal: parseValue(orderTotal),
    subTotal: parseValue(subTotal),
    discount: parseValue(discount),
    tax: parseValue(tax),
  };

  const config = {
    subTotal: {
      label: 'orders:subTotal',
      getValue: () => formatCurrency(parsedValues.subTotal, currency),
    },
    shippingPrice: {
      label: 'orders:shippingPrice',
      getValue: () => formatCurrency(parsedValues.shippingPrice, currency),
    },
    discount: {
      label: 'orders:discount',
      getValue: () => formatCurrency(parsedValues.discount, currency),
    },
    tax: {
      label: 'orders:tax',
      getValue: () => formatCurrency(parsedValues.tax, currency),
    },
    total: {
      label: 'orders:total',
      getValue: () => formatCurrency(parsedValues.orderTotal, currency),
    },
  };

  return (
    <WaveUIAccordion
      defaultExpanded={defaultExpanded}
      title={
        <Box sx={{ display: 'flex', gap: 2, direction: 'ltr' }}>
          <Typography variant='body2'>{t('orders:total')}</Typography>

          <Typography variant='body2' sx={{ fontWeight: '600' }}>
            {formatCurrency(parsedValues.orderTotal, currency)}
          </Typography>
        </Box>
      }
      summaryProps={{
        sx: {
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          direction: 'rtl',
        },
      }}
    >
      <Stack>
        <Typography variant='body2' sx={{ mb: 1, fontWeight: '500' }}>
          {t('orders:cost')}
        </Typography>

        {Object.entries(config).map(([key, field]) => (
          <Stack key={key} direction='row' justifyContent='space-between'>
            <Typography variant='body2' color='text.secondary'>
              {t(field.label)}
            </Typography>
            <Typography variant='body2'>{field.getValue()}</Typography>
          </Stack>
        ))}
      </Stack>
    </WaveUIAccordion>
  );
};

export default Details;
